import { useState } from "react";
import { Check, ChevronsUpDown, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

export default function PatientSelector({
  patients,
  selectedPatient,
  onPatientChange,
  label = "Patient",
  placeholder = "Select patient...",
  showAll = true,
}) {
  const [open, setOpen] = useState(false);

  // Format patient display
  const formatPatientDisplay = (patient) => {
    if (!patient || typeof patient === 'string') return patient || '';
    return `${patient.name} - ${patient.email}`;
  };

  // Get selected patient object or label
  const getSelectedLabel = () => {
    if (selectedPatient === "all") return "All Patients";
    
    const patient = patients.find(p => 
      typeof p === 'string' ? p === selectedPatient : p.id === selectedPatient
    );
    
    if (!patient) return placeholder;
    return formatPatientDisplay(patient);
  };

  // Get search value for a patient
  const getSearchValue = (patient) => {
    if (typeof patient === 'string') return patient;
    
    // Combine all searchable fields into one string
    return `${patient.name || ''} ${patient.email || ''} ${patient.identificationNumber || ''}`.toLowerCase();
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className="flex items-center gap-2 truncate">
              <User className="w-4 h-4" />
              {getSelectedLabel()}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by name, email, or ID..." />
            <CommandList>
              <CommandEmpty>No patient found.</CommandEmpty>
              <CommandGroup>
                {showAll && (
                  <CommandItem
                    value="all"
                    onSelect={() => {
                      onPatientChange("all");
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedPatient === "all" ? "opacity-100" : "opacity-0"
                      )}
                    />
                    All Patients
                  </CommandItem>
                )}
                {patients.map((patient) => {
                  const patientId = typeof patient === 'string' ? patient : patient.id;
                  const patientLabel = formatPatientDisplay(patient);
                  const searchValue = getSearchValue(patient);

                  return (
                    <CommandItem
                      key={patientId}
                      value={searchValue}
                      onSelect={() => {
                        onPatientChange(patientId);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedPatient === patientId ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{patientLabel}</span>
                        {typeof patient !== 'string' && patient.identificationNumber && (
                          <span className="text-xs text-muted-foreground">
                            ID: {patient.identificationNumber}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}